/**
 * ============================================================================
 * SystemConfig 模块 - 常量定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义枚举标签映射（中文显示）
 * - 定义权限常量
 * 
 * 📖 对应后端：
 * - Model: backend/app/Modules/System/Models/SystemConfig.php (getConfigTypes, getConfigGroups方法)
 * - Permissions: backend/database/seeders/Permissions/SystemConfigPermissionSeeder.php
 * 
 * ⚠️ 注意事项：
 * - 标签对象命名：{MODULE}_{ENUM}_LABELS（全大写）
 * - 权限常量命名：{MODULE}_PERMISSIONS（全大写）
 * - 使用 `as const` 保证类型推断
 * - 枚举标签的 key 必须与 enums.ts 中的枚举值对应
 * 
 * 🔄 更新时机：
 * - enums.ts 新增枚举值时（同步新增标签）
 * - 后端权限定义变化时
 * - 中文标签需要修改时
 * 
 * ✅ 验证方式：
 * 让AI执行以下检查：
 * 1. 读取 backend/app/Modules/System/Models/SystemConfig.php
 * 2. 找到 getConfigTypes(), getConfigGroups() 方法
 * 3. 检查本文件的 LABELS 对象是否包含所有映射
 * 4. 检查中文标签是否与后端一致
 * 5. 读取 SystemConfigPermissionSeeder.php，确认权限常量是否完整
 * 
 * @module types/system-config/constants
 * @generated AI-assisted
 * @last-updated 2025-10-18
 * ============================================================================
 */

import { ConfigType, ConfigGroup } from './enums'

/**
 * 配置类型标签映射
 * 
 * 对应后端：SystemConfig::getConfigTypes()
 * 
 * 用于在UI中显示配置类型的中文名称
 */
export const CONFIG_TYPE_LABELS = {
  [ConfigType.STRING]: '字符串',
  [ConfigType.INTEGER]: '整数',
  [ConfigType.FLOAT]: '浮点数',
  [ConfigType.BOOLEAN]: '布尔值',
  [ConfigType.JSON]: 'JSON',
  [ConfigType.ARRAY]: '数组',
} as const

/**
 * 配置分组标签映射
 * 
 * 对应后端：SystemConfig::getConfigGroups()
 * 
 * 用于在UI中显示配置分组的中文名称
 */
export const CONFIG_GROUP_LABELS = {
  [ConfigGroup.SYSTEM]: '系统配置',
  [ConfigGroup.ORDER]: '订单配置',
  [ConfigGroup.SALES]: '销售配置',
  [ConfigGroup.CUSTOMER]: '客户配置',
  [ConfigGroup.MATERIAL]: '物料配置',
  [ConfigGroup.INVENTORY]: '库存配置',
  [ConfigGroup.PRODUCTION]: '生产配置',
  [ConfigGroup.QUALITY]: '质量配置',
  [ConfigGroup.BOM]: 'BOM配置',
  [ConfigGroup.NOTIFICATION]: '通知配置',
  [ConfigGroup.API]: 'API配置',
  [ConfigGroup.AUDIT]: '审计配置',
  [ConfigGroup.FILE]: '文件库配置',
  [ConfigGroup.ANDON]: '安灯配置',
  [ConfigGroup.REPORT]: '报表配置',
  [ConfigGroup.PERMISSION]: '权限配置',
  [ConfigGroup.EQUIPMENT]: '设备配置',
} as const

/**
 * ⚠️ 系统配置权限常量已迁移至统一权限管理
 * 
 * 请从 @bayes-mes/shared 导入标准权限常量：
 * - PERM_SYSTEM_CONFIG_VIEW
 * - PERM_SYSTEM_CONFIG_CREATE
 * - PERM_SYSTEM_CONFIG_UPDATE
 * - PERM_SYSTEM_CONFIG_DELETE
 * 
 * @deprecated 请使用 constants/api-permissions.ts 中的权限常量
 * @see bayes-mes-shared/constants/api-permissions.ts
 */

/**
 * 缓存时间选项（秒）
 * 
 * 用于配置表单的缓存时间选择
 */
export const CACHE_TTL_OPTIONS = [
  { label: '不缓存', value: 0 },
  { label: '30秒', value: 30 },
  { label: '1分钟', value: 60 },
  { label: '5分钟', value: 300 },
  { label: '15分钟', value: 900 },
  { label: '30分钟', value: 1800 },
  { label: '1小时', value: 3600 },
  { label: '2小时', value: 7200 },
  { label: '6小时', value: 21600 },
  { label: '12小时', value: 43200 },
  { label: '24小时', value: 86400 },
] as const

